import { Button } from '@jouzen/ecom-components/v3/Button';
import { Price } from '@jouzen/ecom-components/v3/Price';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useGateValue } from '@statsig/react-bindings';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';
import type { ProductPriceFragment } from '@/queries/fragments/ProductFragment';
import type { ListProduct } from '@/queries/utils/mapProductFragmentToListProduct';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

export interface MaxQuantityReachedMessageProps {
  readonly description: MessageKey;
  readonly fullWidth?: boolean;
  readonly limit: number;
  readonly or4ChargerPrice?: ListProduct['price'] | undefined;
  readonly steloPrice?: ListProduct['price'] | undefined;
}

interface OtherProductCardProps {
  readonly description: MessageKey;
  readonly image: {
    alt: MessageKey;
    src: string;
  };
  readonly price: ProductPriceFragment | null | undefined;
  readonly title: MessageKey;
  readonly url: string;
}

const OtherProductCard = ({
  description,
  image,
  price,
  title,
  url,
}: OtherProductCardProps) => {
  const t = useTranslations();
  const showPrice = price?.amount && price.currencyCode;

  return (
    <Link
      className="flex w-full flex-row items-center gap-3 rounded-lg border border-gray-300 px-[18px] py-4 hover:border-gray-400"
      data-cy="other-product-card"
      href={url}
    >
      <Image
        className="h-[67px] w-[75px] shrink-0 grow-0"
        format="png"
        height={67}
        localizedAlt={image.alt}
        src={image.src}
        width={75}
      />
      <div className="shrink grow">
        <Typography className="text-base font-bold" Element="h3">
          {t(title)}
        </Typography>
        <Typography className="text-sm font-normal">
          {t(description)}
        </Typography>
        {showPrice && (
          <Price
            className="mt-1 text-sm font-normal text-gray-400"
            currencyCode={price.currencyCode!}
            data-cy="other-product-card-price"
            price={price.amount!}
          />
        )}
      </div>
    </Link>
  );
};

const MaxQuantityReachedMessage = ({
  description,
  fullWidth,
  limit,
  or4ChargerPrice,
  steloPrice,
}: MaxQuantityReachedMessageProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const localePartialUrl = getLocalePartialUrl(locale);
  const showDexcomStelo = useGateValue('dexcom_rollout');

  const cartUrl = `${localePartialUrl}cart`;
  const steloUrl = `${localePartialUrl}store/dexcom/stelo-glucose-biosensor`;
  const or4ChargerUrl = `${localePartialUrl}store/accessories/charger-oura-4/select-size`;

  return (
    <section
      className={cx('mt-5 w-full md:mt-3', {
        'mx-auto max-lg:max-w-sm': !fullWidth,
      })}
      data-cy="max-quantity-reached-message"
    >
      <Typography className="mb-6 text-base font-normal">
        <RichText messageKey={description} tValues={{ amount: limit }} />
      </Typography>
      <Button
        className="w-full border-gray-400 hover:border-gray-500"
        href={cartUrl}
        variant="outlined-dark"
        data-cy="view-cart-button"
      >
        <RichText messageKey="view_your_cart" />
      </Button>
      <Typography className="my-6 text-xl font-normal" Element="h2">
        {t('ring_pdp_2025_explore_other_products')}
      </Typography>
      <div className="flex flex-col gap-y-6">
        <OtherProductCard
          description="ring_pdp_2025_charger_set_or4_description"
          image={{
            alt: 'ring_pdp_2025_charger_set_or4_title',
            src: 'or4-pdp/oura-ring-4-extra-charger',
          }}
          price={or4ChargerPrice}
          title="ring_pdp_2025_charger_set_or4_title"
          url={or4ChargerUrl}
        />
        {showDexcomStelo && (
          <OtherProductCard
            description="ring_pdp_2025_stelo_description"
            image={{
              alt: 'ring_pdp_2025_stelo_title',
              src: 'or4-pdp/stelo-glucose-biosensor-2-pack',
            }}
            price={steloPrice}
            title="ring_pdp_2025_stelo_title"
            url={steloUrl}
          />
        )}
      </div>
    </section>
  );
};

export default MaxQuantityReachedMessage;
